<template>
  <div class="prod-stack prod-stack--wide space-y-8 !gap-8">
    <section class="space-y-4">
      <ProductionSectionHead title="Scenes" subtitle="Break the production into the beats you will shoot.">
        <button type="button" class="move-btn-primary shrink-0" @click="openScene()">
          <span class="material-icons text-lg">add</span>
          Add scene
        </button>
      </ProductionSectionHead>

      <div v-if="loading" class="flex justify-center py-12">
        <div class="move-spinner" aria-label="Loading scenes" />
      </div>
      <button
        v-else-if="!scenes.length"
        type="button"
        class="prod-person-empty"
        @click="openScene()"
      >
        <span class="material-icons">movie</span>
        Add the first scene
      </button>
      <ol v-else class="space-y-3">
        <li
          v-for="(scene, index) in scenes"
          :key="scene.id"
          class="prod-scene-card"
        >
          <span class="prod-scene-num">{{ sceneNumber(index) }}</span>
          <div class="min-w-0 flex-1">
            <p class="font-semibold move-text">{{ scene.title }}</p>
            <p v-if="sceneMeta(scene)" class="mt-0.5 text-xs font-semibold uppercase tracking-wider move-text-faint">
              {{ sceneMeta(scene) }}
            </p>
            <p v-if="scene.description" class="mt-2 text-sm move-text-muted whitespace-pre-wrap">{{ scene.description }}</p>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button
              type="button"
              class="move-icon-btn"
              aria-label="Move up"
              :disabled="index === 0"
              @click="moveScene(index, -1)"
            >
              <span class="material-icons">expand_less</span>
            </button>
            <button
              type="button"
              class="move-icon-btn"
              aria-label="Move down"
              :disabled="index === scenes.length - 1"
              @click="moveScene(index, 1)"
            >
              <span class="material-icons">expand_more</span>
            </button>
            <button type="button" class="move-icon-btn" aria-label="Edit scene" @click="openScene(scene)">
              <span class="material-icons">edit</span>
            </button>
            <button type="button" class="move-icon-btn move-icon-btn--danger" aria-label="Remove scene" @click="askRemoveScene(scene)">
              <span class="material-icons">close</span>
            </button>
          </div>
        </li>
      </ol>
    </section>

    <section class="space-y-4">
      <ProductionSectionHead title="Mood board" subtitle="References for look, light, wardrobe, and locations." />

      <form class="move-card prod-panel space-y-4" @submit.prevent="addMood">
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="move-field !mb-0">
            <label for="mood-file">Upload image</label>
            <input id="mood-file" ref="moodFileInput" class="block w-full text-sm move-text-muted" type="file" accept="image/*" @change="onMoodFile" />
          </div>
          <div class="move-field !mb-0">
            <label for="mood-url">Or paste image URL</label>
            <input id="mood-url" v-model="moodForm.image_url" type="text" inputmode="url" class="move-input" placeholder="https://…" />
          </div>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="move-field !mb-0">
            <label for="mood-caption">Caption</label>
            <input id="mood-caption" v-model="moodForm.caption" class="move-input" placeholder="Warm tungsten, market textures…" />
          </div>
          <div class="move-field !mb-0">
            <label for="mood-scene">Tag a scene</label>
            <MoveSelect
              id="mood-scene"
              v-model="moodForm.production_scene_id"
              block
              :options="sceneFilterOptions"
            />
          </div>
        </div>
        <p v-if="moodError" class="move-alert-error">{{ moodError }}</p>
        <button type="submit" class="move-btn-primary" :disabled="moodSaving">
          <span class="material-icons text-lg">{{ moodSaving ? 'sync' : 'add_photo_alternate' }}</span>
          {{ moodSaving ? 'Adding…' : 'Add to board' }}
        </button>
      </form>

      <div v-if="mood.length" class="flex flex-wrap gap-2">
        <button
          v-for="chip in moodFilters"
          :key="chip.value"
          type="button"
          class="prod-chip !py-1.5 !px-3 !gap-1.5"
          :class="{ 'is-on': moodFilter === chip.value }"
          @click="moodFilter = chip.value"
        >
          <span class="text-xs font-semibold">{{ chip.label }}</span>
        </button>
      </div>

      <div v-if="!mood.length && !loading" class="move-card move-empty-state py-12">
        <span class="material-icons">filter_frames</span>
        <p class="font-semibold move-text">No references yet</p>
        <p class="text-sm move-text-faint mt-1">Upload a still or paste a link to start the look.</p>
      </div>
      <div v-else-if="filteredMood.length" class="prod-mood-grid">
        <article
          v-for="item in filteredMood"
          :key="item.id"
          class="prod-mood-tile"
        >
          <button type="button" class="prod-mood-tile__image" @click="preview = item">
            <img :src="item.image_url" :alt="item.caption || 'Mood reference'" />
          </button>
          <div class="prod-mood-tile__meta">
            <p class="text-sm font-semibold move-text truncate">{{ item.caption || 'Reference' }}</p>
            <p v-if="item.scene" class="text-xs move-text-faint truncate">{{ item.scene.title }}</p>
          </div>
          <button
            type="button"
            class="prod-mood-tile__remove"
            aria-label="Remove reference"
            @click="askRemoveMood(item)"
          >
            <span class="material-icons">close</span>
          </button>
        </article>
      </div>
    </section>

    <teleport to="body">
      <div
        v-if="showScene"
        class="fixed inset-0 move-modal-overlay flex items-center justify-center z-50 p-4"
        @click.self="closeScene"
      >
        <div class="move-card move-modal-card w-full max-w-lg" role="dialog" :aria-labelledby="sceneForm.id ? 'edit-scene-title' : 'add-scene-title'">
          <div class="move-modal-card__header">
            <div>
              <h3 :id="sceneForm.id ? 'edit-scene-title' : 'add-scene-title'" class="move-modal-card__title">
                {{ sceneForm.id ? 'Edit scene' : 'Add scene' }}
              </h3>
              <p class="move-modal-card__subtitle">Title, setting, and what happens.</p>
            </div>
            <button type="button" class="move-icon-btn" aria-label="Close" @click="closeScene">
              <span class="material-icons">close</span>
            </button>
          </div>
          <form class="space-y-4" @submit.prevent="saveScene">
            <div class="move-field">
              <label for="scene-title">Title</label>
              <input id="scene-title" v-model="sceneForm.title" required class="move-input" placeholder="Opening market walk" />
            </div>
            <div class="move-field">
              <label for="scene-description">What happens</label>
              <textarea id="scene-description" v-model="sceneForm.description" rows="4" class="move-textarea" placeholder="Presenter walks the stalls and tastes fruit." />
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="move-field">
                <label for="scene-location">Location</label>
                <input id="scene-location" v-model="sceneForm.location" class="move-input" placeholder="Kamuzu Market" />
              </div>
              <div class="move-field">
                <label for="scene-setting">Setting</label>
                <MoveSelect id="scene-setting" v-model="sceneForm.setting" block :options="sceneSettingOptions" />
              </div>
            </div>
            <div class="move-field">
              <label for="scene-time">Time of day</label>
              <MoveSelect id="scene-time" v-model="sceneForm.time_of_day" block :options="sceneTimeOptions" />
            </div>
            <div class="move-field">
              <label for="scene-notes">Notes</label>
              <textarea id="scene-notes" v-model="sceneForm.notes" rows="2" class="move-textarea" placeholder="Need a second camera for B-roll." />
            </div>
            <p v-if="sceneError" class="move-alert-error">{{ sceneError }}</p>
            <div class="prod-form-actions">
              <button type="submit" class="move-btn-primary flex-1" :disabled="sceneSaving">
                <span class="material-icons text-lg">{{ sceneSaving ? 'sync' : 'check' }}</span>
                {{ sceneForm.id ? 'Save scene' : 'Add scene' }}
              </button>
              <button type="button" class="move-btn-ghost" @click="closeScene">Cancel</button>
            </div>
          </form>
        </div>
      </div>

      <div
        v-if="preview"
        class="fixed inset-0 move-modal-overlay flex items-center justify-center z-50 p-4"
        @click.self="preview = null"
      >
        <div class="prod-mood-preview">
          <img :src="preview.image_url" :alt="preview.caption || 'Mood reference'" />
          <div class="flex items-start justify-between gap-3 mt-3">
            <div class="min-w-0">
              <p class="font-semibold move-text">{{ preview.caption || 'Reference' }}</p>
              <p v-if="preview.scene" class="text-sm move-text-faint">{{ preview.scene.title }}</p>
            </div>
            <button type="button" class="move-icon-btn" aria-label="Close preview" @click="preview = null">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <MoveConfirmDialog
      v-model="showRemoveSceneModal"
      title="Delete scene"
      subtitle="This action cannot be undone."
      :detail-title="sceneToRemove?.title || 'This scene'"
      detail-body="Mood references linked only to this scene will stay on the board."
      detail-tone="danger"
      confirm-label="Delete scene"
      confirm-icon="delete"
      busy-label="Deleting…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemoveScene"
      @cancel="closeRemoveScene"
    />

    <MoveConfirmDialog
      v-model="showRemoveMoodModal"
      title="Remove reference"
      subtitle="This action cannot be undone."
      :detail-title="moodToRemove?.caption || 'This reference'"
      detail-tone="danger"
      confirm-label="Remove"
      confirm-icon="delete"
      busy-label="Removing…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemoveMood"
      @cancel="closeRemoveMood"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import MoveConfirmDialog from '../../components/MoveConfirmDialog.vue'
import { productionApi } from '../../services/productionApi'
import {
  sceneSettingLabels,
  sceneSettingOptions,
  sceneTimeLabels,
  sceneTimeOptions
} from '../../config/productionNavigation'

const workspace = inject('productionWorkspace')
const loading = ref(true)
const scenes = ref([])
const mood = ref([])
const showScene = ref(false)
const sceneSaving = ref(false)
const sceneError = ref('')
const moodSaving = ref(false)
const moodError = ref('')
const moodFilter = ref('all')
const preview = ref(null)
const moodFile = ref(null)
const moodFileInput = ref(null)
const sceneForm = reactive({
  id: null,
  title: '',
  description: '',
  location: '',
  setting: '',
  time_of_day: '',
  notes: ''
})
const moodForm = reactive({
  image_url: '',
  caption: '',
  production_scene_id: ''
})

const sceneFilterOptions = computed(() => [
  { value: '', label: 'Whole production' },
  ...scenes.value.map((scene, index) => ({
    value: String(scene.id),
    label: `${sceneNumber(index)} ${scene.title}`
  }))
])

const moodFilters = computed(() => [
  { value: 'all', label: 'All' },
  { value: 'untagged', label: 'Untagged' },
  ...scenes.value.map((scene, index) => ({
    value: String(scene.id),
    label: `${sceneNumber(index)} ${scene.title}`
  }))
])

const filteredMood = computed(() => {
  if (moodFilter.value === 'all') return mood.value
  if (moodFilter.value === 'untagged') return mood.value.filter((item) => !item.production_scene_id)
  return mood.value.filter((item) => String(item.production_scene_id) === moodFilter.value)
})

function sceneNumber (index) {
  return String(index + 1).padStart(2, '0')
}

function sceneMeta (scene) {
  return [
    scene.setting ? sceneSettingLabels[scene.setting] : null,
    scene.time_of_day ? sceneTimeLabels[scene.time_of_day] : null,
    scene.location
  ].filter(Boolean).join(' · ')
}

function resetSceneForm () {
  sceneForm.id = null
  sceneForm.title = ''
  sceneForm.description = ''
  sceneForm.location = ''
  sceneForm.setting = ''
  sceneForm.time_of_day = ''
  sceneForm.notes = ''
  sceneError.value = ''
}

function openScene (scene = null) {
  resetSceneForm()
  if (scene) {
    sceneForm.id = scene.id
    sceneForm.title = scene.title || ''
    sceneForm.description = scene.description || ''
    sceneForm.location = scene.location || ''
    sceneForm.setting = scene.setting || ''
    sceneForm.time_of_day = scene.time_of_day || ''
    sceneForm.notes = scene.notes || ''
  }
  showScene.value = true
}

function closeScene () {
  showScene.value = false
  resetSceneForm()
}

function scenePayload () {
  return {
    title: sceneForm.title,
    description: sceneForm.description || null,
    location: sceneForm.location || null,
    setting: sceneForm.setting || null,
    time_of_day: sceneForm.time_of_day || null,
    notes: sceneForm.notes || null
  }
}

async function load () {
  loading.value = true
  try {
    const [scenesBody, moodBody] = await Promise.all([
      productionApi.listScenes(workspace.productionId.value),
      productionApi.listMoodBoard(workspace.productionId.value)
    ])
    scenes.value = scenesBody.data || []
    mood.value = moodBody.data || []
  } finally {
    loading.value = false
  }
}

async function saveScene () {
  sceneError.value = ''
  sceneSaving.value = true
  try {
    if (sceneForm.id) {
      await productionApi.updateScene(workspace.productionId.value, sceneForm.id, scenePayload())
    } else {
      await productionApi.createScene(workspace.productionId.value, scenePayload())
    }
    closeScene()
    await load()
  } catch (e) {
    sceneError.value = e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' ') || 'Could not save scene.'
  } finally {
    sceneSaving.value = false
  }
}

const showRemoveSceneModal = ref(false)
const sceneToRemove = ref(null)
const showRemoveMoodModal = ref(false)
const moodToRemove = ref(null)
const removing = ref(false)
const removeError = ref('')

function askRemoveScene (scene) {
  sceneToRemove.value = scene
  removeError.value = ''
  showRemoveSceneModal.value = true
}

function closeRemoveScene () {
  if (removing.value) return
  showRemoveSceneModal.value = false
  sceneToRemove.value = null
  removeError.value = ''
}

async function confirmRemoveScene () {
  const scene = sceneToRemove.value
  if (!scene || removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    await productionApi.deleteScene(workspace.productionId.value, scene.id)
    if (moodFilter.value === String(scene.id)) moodFilter.value = 'all'
    showRemoveSceneModal.value = false
    sceneToRemove.value = null
    await load()
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not delete this scene.'
  } finally {
    removing.value = false
  }
}

async function moveScene (index, delta) {
  const next = index + delta
  if (next < 0 || next >= scenes.value.length) return
  const ids = scenes.value.map((scene) => scene.id)
  const [id] = ids.splice(index, 1)
  ids.splice(next, 0, id)
  await productionApi.reorderScenes(workspace.productionId.value, ids)
  await load()
}

function onMoodFile (event) {
  moodFile.value = event.target.files?.[0] || null
}

function resetMoodForm () {
  moodForm.image_url = ''
  moodForm.caption = ''
  moodForm.production_scene_id = ''
  moodFile.value = null
  moodError.value = ''
  if (moodFileInput.value) moodFileInput.value.value = ''
}

async function addMood () {
  moodError.value = ''
  if (!moodFile.value && !moodForm.image_url) {
    moodError.value = 'Upload an image or paste an image URL.'
    return
  }
  moodSaving.value = true
  try {
    await productionApi.addMoodBoardItem(workspace.productionId.value, {
      file: moodFile.value,
      image_url: moodForm.image_url || null,
      caption: moodForm.caption || null,
      production_scene_id: moodForm.production_scene_id || null
    })
    resetMoodForm()
    await load()
  } catch (e) {
    moodError.value = e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' ') || 'Could not add reference.'
  } finally {
    moodSaving.value = false
  }
}

function askRemoveMood (item) {
  moodToRemove.value = item
  removeError.value = ''
  showRemoveMoodModal.value = true
}

function closeRemoveMood () {
  if (removing.value) return
  showRemoveMoodModal.value = false
  moodToRemove.value = null
  removeError.value = ''
}

async function confirmRemoveMood () {
  const item = moodToRemove.value
  if (!item || removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    await productionApi.deleteMoodBoardItem(workspace.productionId.value, item.id)
    if (preview.value?.id === item.id) preview.value = null
    showRemoveMoodModal.value = false
    moodToRemove.value = null
    await load()
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not remove this reference.'
  } finally {
    removing.value = false
  }
}

onMounted(load)
</script>
